// createAesEmbeddedManifestDataUrl.js
'use strict';

import { captureException } from '@sentry/browser';

import fetch from '../resource/customFetch.js';
import { player as playerDebug } from '../resource/debug.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getPlaybackLogPrefix } from '../resource/playerLogUtils.js';
import raceWithTimeout from '../resource/raceWithTimeout.js';
import { unwrapAesKey } from '../resource/responseDecryptionUtil.js';

// The playlists here are walked line by line rather than through the project's
// `parseM3u8.js` (which wraps `m3u8-parser`), because the job is to hand the
// player back a rewritten manifest, not to read one: every line that is not
// replaced has to survive verbatim. `m3u8-parser` returns a structured object
// with no line numbers and no way back to text, so rebuilding a manifest from
// it would mean re-serialising tags this code never looked at. Reach for
// `parseM3u8.js` when the question is "what is in this manifest"; keep to text
// when the answer has to be another manifest.

const playbackBaseLog = playerDebug.extend('log:playback');
const playbackBaseError = playerDebug.extend('error:playback');

// customFetch carries no timeout, so a request that never finishes would leave
// the `Promise.all` over the ladder waiting forever — one stuck tier taking down
// a short whose lowest tier and key are already in hand. With a deadline, a
// variant that runs out of time is dropped like any other failed tier.
const MANIFEST_FETCH_TIMEOUT_MS = 8000;

/**
 * Fetch a URL and read its body, both under one deadline. `fetch` resolving only
 * means the headers arrived, so the body has to be inside the deadline too: a
 * response that stops midway would otherwise run without any limit and leave the
 * `Promise.all` over the ladder pending forever — the very hang the deadline is
 * here to prevent.
 *
 * @param {string} url
 * @param {object} [options] - Passed through to `customFetch`.
 * @returns {Promise<{ ok: boolean, status: number, text: string }>} `text` is empty when the response is not ok.
 */
const fetchWithDeadline = async (url, options) => {
  const abortController = new AbortController();
  const readResponse = async () => {
    const response = await fetch(url, {
      ...options,
      signal: abortController.signal,
    });
    // A bad status becomes the caller's own error, so the body it carries is
    // never read — one less thing that can stall.
    return {
      ok: response.ok,
      status: response.status,
      text: response.ok ? await response.text() : '',
    };
  };

  try {
    return await raceWithTimeout({
      promise: readResponse(),
      ms: MANIFEST_FETCH_TIMEOUT_MS,
      label: 'aes_embedded_manifest_fetch',
      onTimeout: () => abortController.abort(),
    });
  } catch (error) {
    // Worded to read as a fetch failure to the stage lookup in the caller. The
    // abort can surface first when the platform propagates it to the body read.
    if ('TimeoutError' === error?.name || 'AbortError' === error?.name) {
      throw new Error(
        `aes_embedded_manifest_fetch_error [timeout ${MANIFEST_FETCH_TIMEOUT_MS}ms] ${url}`
      );
    }
    throw error;
  }
};

const uint8ToBase64 = bytes => {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const ABSOLUTE_URI_SCHEME_REGEX = /^[a-z][a-z0-9+.-]*:/i;
const absolutizeUri = (uri, baseUrl) => {
  if (!uri || ABSOLUTE_URI_SCHEME_REGEX.test(uri)) return uri;
  try {
    return new URL(uri, baseUrl).href;
  } catch (error) {
    return uri;
  }
};

const CODECS_ATTRIBUTE_REGEX = /(?:^|[:,])\s*CODECS="([^"]+)"/;
// Same pattern, two jobs that cannot share one regex: the non-global one is for
// `match` (a global regex returns every match instead of the capture group) and
// for replacing only the first occurrence; the global one is for rewriting every
// `URI=` on a line. Keep them in sync.
const URI_ATTRIBUTE_REGEX = /URI="([^"]+)"/;
const URI_ATTRIBUTE_GLOBAL_REGEX = /URI="([^"]+)"/g;
const STREAM_INF_TAG = '#EXT-X-STREAM-INF';
const MEDIA_TAG = '#EXT-X-MEDIA';
const AUDIO_TYPE_ATTRIBUTE_REGEX = /[:,]TYPE=AUDIO\b/;
// `#EXT-X-KEY` in a media playlist and `#EXT-X-SESSION-KEY` in a master carry
// the same `METHOD=...,URI="..."` shape, so one pattern covers both. The `\b`
// keeps it off tags that merely start with KEY, e.g. `#EXT-X-KEYFORMAT`.
const KEY_TAG_REGEX = /^#EXT-X-(?:SESSION-)?KEY\b/;
const TARGET_DURATION_TAG = '#EXT-X-TARGETDURATION';
const TARGET_DURATION_TAG_REGEX = /^#EXT-X-TARGETDURATION:(\d+)/;
const SEGMENT_DURATION_TAG_REGEX = /^#EXTINF:([\d.]+)/gm;
const HEADER_TAG = '#EXTM3U';
const VERSION_TAG = '#EXT-X-VERSION';
const VERSION_TAG_REGEX = /^#EXT-X-VERSION:(\d+)/;
const DEFINE_TAG = '#EXT-X-DEFINE';
const VARIABLE_REFERENCE_PREFIX = '{$';
// One character, because the name is spent on every segment line.
const SEGMENT_PREFIX_VARIABLE_NAME = 'S';
const SEGMENT_PREFIX_VARIABLE_REFERENCE = `{$${SEGMENT_PREFIX_VARIABLE_NAME}}`;
// `#EXT-X-DEFINE` arrived with protocol version 8, so a playlist using it has to
// declare at least that.
const DEFINE_MINIMUM_VERSION = 8;

const getLongestCommonPrefix = values => {
  let prefix = values[0] || '';
  for (const value of values) {
    while (prefix && !value.startsWith(prefix)) prefix = prefix.slice(0, -1);
    if (!prefix) return '';
  }
  return prefix;
};

/**
 * Factor the shared head of every segment URI into one `#EXT-X-DEFINE`, leaving
 * each segment line as `{$S}` plus what makes it unique. Absolutizing 600
 * segment URIs is what makes a long asset's manifest big, and this hands the
 * player the same URIs at about a third of the bytes.
 *
 * Returns the manifest unchanged when there is nothing to gain: no shared head
 * at all, or a playlist that already defines variables of its own, where reusing
 * a name would have parsers reject the playlist outright. Whether the rewrite
 * actually paid for itself is the caller's to check — a playlist short enough
 * for the tag to cost more than the references save keeps its URIs written out.
 *
 * @param {string} manifestText - A media playlist with absolute segment URIs.
 * @returns {string}
 * @example
 * compressSegmentUrisWithDefine('#EXTM3U\n#EXT-X-VERSION:5\n#EXTINF:3.000,\nhttps://cdn/a/sd0.ts');
 * // => '#EXTM3U\n#EXT-X-VERSION:8\n#EXT-X-DEFINE:NAME="S",VALUE="https://cdn/a/sd"\n#EXTINF:3.000,\n{$S}0.ts'
 */
const compressSegmentUrisWithDefine = manifestText => {
  const lines = manifestText.split('\n');
  if (
    lines.some(
      line =>
        line.startsWith(DEFINE_TAG) || line.includes(VARIABLE_REFERENCE_PREFIX)
    )
  ) {
    return manifestText;
  }

  const segmentLineIndices = new Set();
  lines.forEach((line, lineIndex) => {
    const trimmedLine = line.trim();
    if (trimmedLine && !trimmedLine.startsWith('#'))
      segmentLineIndices.add(lineIndex);
  });
  const segmentPrefix = getLongestCommonPrefix(
    [...segmentLineIndices].map(lineIndex => lines[lineIndex])
  );
  if (!segmentPrefix) return manifestText;

  const compressedLines = lines.map((line, lineIndex) =>
    segmentLineIndices.has(lineIndex)
      ? `${SEGMENT_PREFIX_VARIABLE_REFERENCE}${line.slice(segmentPrefix.length)}`
      : line
  );

  // The variable has to be declared before the first line that reads it, and the
  // version it needs before that: both belong at the top, next to whichever of
  // the two tags the playlist already carries.
  const defineLine = `${DEFINE_TAG}:NAME="${SEGMENT_PREFIX_VARIABLE_NAME}",VALUE="${segmentPrefix}"`;
  const versionLineIndex = compressedLines.findIndex(line =>
    line.startsWith(VERSION_TAG)
  );
  if (versionLineIndex >= 0) {
    compressedLines[versionLineIndex] = `${VERSION_TAG}:${Math.max(
      DEFINE_MINIMUM_VERSION,
      Number(compressedLines[versionLineIndex].match(VERSION_TAG_REGEX)?.[1]) ||
        0
    )}`;
    compressedLines.splice(versionLineIndex + 1, 0, defineLine);
  } else {
    const headerLineIndex = compressedLines.findIndex(line =>
      line.startsWith(HEADER_TAG)
    );
    compressedLines.splice(
      headerLineIndex + 1,
      0,
      `${VERSION_TAG}:${DEFINE_MINIMUM_VERSION}`,
      defineLine
    );
  }

  return compressedLines.join('\n');
};

/**
 * The `#EXT-X-TARGETDURATION` a playlist's own segments require. HLS says every
 * segment's `#EXTINF`, rounded to the nearest integer, must be no longer than
 * the declared target duration, and Safari enforces that by refusing the
 * playlist outright — without an error the page can see, so the player simply
 * never reports `canplay`. Our packager writes the target from the nominal
 * segment length while flushing a longer tail segment, which is exactly that
 * violation: one 29-minute asset declares 3 with a closing segment of 6.395.
 *
 * @param {string} manifestText
 * @returns {number} 0 when the playlist has no segment.
 */
const getRequiredTargetDuration = manifestText => {
  let requiredTargetDuration = 0;
  for (const [, duration] of manifestText.matchAll(
    SEGMENT_DURATION_TAG_REGEX
  )) {
    requiredTargetDuration = Math.max(
      requiredTargetDuration,
      Math.round(Number(duration))
    );
  }
  return requiredTargetDuration;
};

/**
 * Whether a master-level line points at a playlist that has to be inlined.
 * Only the audio rendition qualifies: it is AES-encrypted like the video tiers,
 * so playback needs its key embedded the same way. Subtitle, closed-caption and
 * I-frame playlists are left as plain absolute URLs instead — inlining them
 * costs one more request per short each, and a single 404 among them would take
 * a perfectly playable asset down over a missing caption or trick-play track.
 */
const getIsInlinableRenditionLine = line =>
  line.startsWith(MEDIA_TAG) && AUDIO_TYPE_ATTRIBUTE_REGEX.test(line);

const absolutizeUriAttributes = (line, baseUrl) =>
  line.replace(
    URI_ATTRIBUTE_GLOBAL_REGEX,
    (_, uri) => `URI="${absolutizeUri(uri, baseUrl)}"`
  );

const toManifestDataUrl = manifestText =>
  `data:application/vnd.apple.mpegurl;base64,${uint8ToBase64(
    new TextEncoder().encode(manifestText)
  )}`;

// The `#EXT-X-STREAM-INF` describing a variant sits on an earlier line than its URI.
const findStreamInfoLineIndex = ({ lines, uriLineIndex }) => {
  for (let index = uriLineIndex - 1; index >= 0; index -= 1) {
    if (lines[index].trim().startsWith(STREAM_INF_TAG)) return index;
  }
  return -1;
};

// Key endpoint returns { nonce, wrapped_key }, not raw bytes.
const fetchAndUnwrapAesKey = async ({
  keyUrl,
  authHeaders,
  clientId,
  sessionId,
  messageId,
  metadata,
}) => {
  let rewrittenKeyUrl = keyUrl;
  try {
    const urlObj = new URL(keyUrl);
    urlObj.host = new URL(getResourceUrl({ endpoint: '/' }).href).host;
    urlObj.pathname = urlObj.pathname.replace(
      /\/messages\/[^/]+\//,
      `/messages/${messageId}/`
    );
    rewrittenKeyUrl = urlObj.href;
  } catch (error) {
    // fall back to original keyUrl
  }

  let keyResponse;
  try {
    keyResponse = await fetchWithDeadline(rewrittenKeyUrl, {
      method: 'GET',
      headers: authHeaders,
    });
  } catch (error) {
    error.stage = 'key.fetch';
    throw error;
  }
  if (!keyResponse.ok) {
    const error = new Error(
      `aes_embedded_manifest_key_error [${keyResponse.status}] ${rewrittenKeyUrl}`
    );
    error.stage = 'key.fetch';
    throw error;
  }

  try {
    const { nonce, wrapped_key: wrappedKey } = JSON.parse(keyResponse.text);
    const rawKey = await unwrapAesKey({
      clientId,
      sessionId,
      nonce,
      wrappedKey,
    });
    metadata.keyCount = (metadata.keyCount || 0) + 1;
    // Length is a health check on the unwrap, not per-key data. Recording it
    // once keeps a ladder with one key per tier from having its log decided
    // by whichever key happened to finish last.
    if (!metadata.keyByteLength) metadata.keyByteLength = rawKey?.byteLength;
    return rawKey;
  } catch (error) {
    const wrapped = new Error(
      `aes_embedded_manifest_key_error: ${error.message} ${rewrittenKeyUrl}`,
      { cause: error }
    );
    wrapped.stage = 'key.unwrap';
    throw wrapped;
  }
};

/**
 * Replace the URI of a key tag with the key itself as a data URL, so the player
 * never has to make the authenticated key request on its own.
 *
 * @param {object} parameters
 * @param {string} parameters.line - The `#EXT-X-KEY` / `#EXT-X-SESSION-KEY` line.
 * @param {Map<string, Promise<ArrayBuffer>>} parameters.keyCache - Shared across one source build.
 * @returns {Promise<string>} The line with its URI inlined, or unchanged if it has none.
 * @throws {Error} If the key cannot be fetched or unwrapped.
 */
const inlineAesKeyLine = async ({
  line,
  authHeaders,
  clientId,
  sessionId,
  messageId,
  metadata,
  keyCache,
}) => {
  const uriMatch = line.match(URI_ATTRIBUTE_REGEX);
  if (!uriMatch) return line;
  const [, keyUrl] = uriMatch;
  if (keyUrl.startsWith('data:')) return line;

  // Cached by the URI as the manifest writes it: a key shared by the whole
  // ladder is unwrapped once, and a ladder carrying one key per tier unwraps
  // each of them. Keying on that text rather than on the rewritten request URL
  // is deliberate — it holds whichever way the backend ends up telling the
  // per-quality keys apart, at the cost of one extra request in the case where
  // two spellings of a URI turn out to name the same key.
  if (!keyCache.has(keyUrl)) {
    keyCache.set(
      keyUrl,
      fetchAndUnwrapAesKey({
        keyUrl,
        authHeaders,
        clientId,
        sessionId,
        messageId,
        metadata,
      })
    );
  }
  const rawKey = await keyCache.get(keyUrl);
  const inlined = `data:application/octet-stream;base64,${uint8ToBase64(new Uint8Array(rawKey))}`;
  return line.replace(URI_ATTRIBUTE_REGEX, `URI="${inlined}"`);
};

const rewriteMediaPlaylist = async ({
  manifestText,
  manifestUrl,
  metadata,
  ...keyParameters
}) => {
  const requiredTargetDuration = getRequiredTargetDuration(manifestText);

  const rewriteLine = line => {
    if (KEY_TAG_REGEX.test(line))
      return inlineAesKeyLine({ ...keyParameters, metadata, line });
    const declaredTargetDuration = line.match(TARGET_DURATION_TAG_REGEX)?.[1];
    if (declaredTargetDuration) {
      if (Number(declaredTargetDuration) >= requiredTargetDuration) return line;
      metadata.repairedTargetDuration = {
        declared: Number(declaredTargetDuration),
        required: requiredTargetDuration,
      };
      return `${TARGET_DURATION_TAG}:${requiredTargetDuration}`;
    }
    if (line.startsWith('#')) return absolutizeUriAttributes(line, manifestUrl);
    if (line.trim() === '') return line;
    return absolutizeUri(line.trim(), manifestUrl);
  };

  const rewritten = await Promise.all(
    manifestText.split(/\r?\n/).map(rewriteLine)
  );
  const rewrittenText = rewritten.join('\n');

  // Whether the rewrite is worth having is the rewrite's own answer: a playlist
  // with enough segments to matter comes back smaller, and a short one comes
  // back longer because the `#EXT-X-DEFINE` costs more than the references save.
  // No size threshold to keep in step with what players actually accept.
  const compressedText = compressSegmentUrisWithDefine(rewrittenText);
  if (compressedText.length >= rewrittenText.length) return rewrittenText;
  metadata.hasCompressedSegmentUris = true;
  return compressedText;
};

/**
 * Inline every media playlist a master references so the player keeps the full
 * ABR ladder. Each referenced playlist gets its AES key embedded and becomes a
 * data URL in place; tiers that fail to load are dropped from the ladder. A
 * master-level `#EXT-X-SESSION-KEY` is inlined too, and left as an absolute URL
 * if its key cannot be reached.
 *
 * Sets `metadata.variantNames`, `metadata.variantCount`, `metadata.hlsCodecs`
 * and `metadata.droppedVariants`.
 *
 * @param {object} parameters
 * @param {string} parameters.manifestText - Raw M3U8 master playlist text.
 * @param {string} parameters.manifestUrl - URL the master was fetched from.
 * @returns {Promise<string>} Rewritten master playlist text.
 * @throws {Error} If the master has no variant, or every variant failed.
 */
const rewriteMasterPlaylist = async ({
  manifestText,
  manifestUrl,
  metadata,
  ...keyParameters
}) => {
  const lines = manifestText.split(/\r?\n/);
  const references = [];
  // A master-level `#EXT-X-SESSION-KEY` announces the ladder's key before any
  // media playlist is read, and the player would go and fetch that URL itself
  // — without the headers the key endpoint requires. Inline it through the same
  // cache the tiers use, so a key both levels name is still unwrapped once.
  // Started here rather than in the rewrite pass below so the request goes out
  // alongside the media playlists instead of a round trip after all of them
  // have landed. A failure is not fatal: every tier carries its own
  // `#EXT-X-KEY`, so fall back to the absolute URL rather than take down an
  // asset that can still play.
  const keyLineByIndex = new Map();

  lines.forEach((line, lineIndex) => {
    const trimmedLine = line.trim();
    if (!trimmedLine) return;

    if (getIsInlinableRenditionLine(trimmedLine)) {
      const uriMatch = trimmedLine.match(URI_ATTRIBUTE_REGEX);
      if (uriMatch) {
        references.push({ lineIndex, uri: uriMatch[1], isVariant: false });
      }
      return;
    }

    if (KEY_TAG_REGEX.test(trimmedLine)) {
      keyLineByIndex.set(
        lineIndex,
        inlineAesKeyLine({ ...keyParameters, metadata, line }).catch(() =>
          absolutizeUriAttributes(line, manifestUrl)
        )
      );
      return;
    }

    if (!trimmedLine.startsWith('#')) {
      references.push({ lineIndex, uri: trimmedLine, isVariant: true });
    }
  });

  if (!references.some(reference => reference.isVariant)) {
    throw new Error('aes_embedded_manifest_master_without_variant');
  }

  const results = await Promise.all(
    references.map(async reference => {
      try {
        const resolvedUrl = new URL(reference.uri, manifestUrl).href;
        const renditionResponse = await fetchWithDeadline(resolvedUrl, {
          method: 'GET',
        });
        if (!renditionResponse.ok) {
          throw new Error(
            `aes_embedded_manifest_fetch_error [${renditionResponse.status}] ${resolvedUrl}`
          );
        }
        const rewritten = await rewriteMediaPlaylist({
          ...keyParameters,
          metadata,
          manifestText: renditionResponse.text,
          manifestUrl: resolvedUrl,
        });
        return { reference, dataUrl: toManifestDataUrl(rewritten) };
      } catch (error) {
        return { reference, error };
      }
    })
  );

  // Where a master does split audio out into its own rendition, that group
  // cannot be dropped the way a tier can: every `#EXT-X-STREAM-INF` names it
  // through AUDIO=, so deleting the `#EXT-X-MEDIA` line leaves a master pointing
  // at a group that no longer exists, and the video-only tiers behind it carry no
  // sound of their own. Fail the build and let the caller's existing error
  // handling take over. Today's transcoder ships muxed variants instead, so this
  // is for the masters that do not.
  const failedAudioReference = results.find(
    ({ reference, error }) => error && !reference.isVariant
  );
  if (failedAudioReference) throw failedAudioReference.error;

  // A single broken tier should not take the whole asset down with it.
  const droppedLineIndices = new Set();
  const lineReplacementByIndex = new Map();
  let firstError = null;
  metadata.droppedVariants = [];

  results.forEach(({ reference, dataUrl, error }) => {
    const { lineIndex, isVariant } = reference;

    if (error) {
      if (!firstError) firstError = error;
      droppedLineIndices.add(lineIndex);
      metadata.droppedVariants.push({
        uri: reference.uri,
        errorMessage: error.message,
      });
      if (isVariant) {
        // Drop the `#EXT-X-STREAM-INF` that would otherwise describe nothing.
        const streamInfoLineIndex = findStreamInfoLineIndex({
          lines,
          uriLineIndex: lineIndex,
        });
        if (streamInfoLineIndex >= 0)
          droppedLineIndices.add(streamInfoLineIndex);
      }
      return;
    }

    lineReplacementByIndex.set(
      lineIndex,
      isVariant
        ? dataUrl
        : lines[lineIndex].replace(URI_ATTRIBUTE_REGEX, `URI="${dataUrl}"`)
    );
  });

  const keptVariants = results.filter(
    ({ reference, error }) => reference.isVariant && !error
  );
  if (!keptVariants.length) throw firstError;

  metadata.variantNames = keptVariants.map(({ reference }) => reference.uri);
  metadata.variantCount = keptVariants.length;
  // The master carries per-variant CODECS, so this is only the player's
  // fallback when it cannot read them; the first kept tier is enough.
  metadata.hlsCodecs =
    lines[
      findStreamInfoLineIndex({
        lines,
        uriLineIndex: keptVariants[0].reference.lineIndex,
      })
    ]?.match(CODECS_ATTRIBUTE_REGEX)?.[1] || null;

  const rewrittenLines = await Promise.all(
    lines.map((line, lineIndex) => {
      if (droppedLineIndices.has(lineIndex)) return null;

      const replacement = lineReplacementByIndex.get(lineIndex);
      if (undefined !== replacement) return replacement;

      const keyLine = keyLineByIndex.get(lineIndex);
      if (keyLine) return keyLine;

      // A master and its media playlists should declare the same compatibility
      // version, so a tier that had to reach for `#EXT-X-DEFINE` raises the
      // master with it.
      const declaredVersion = line.match(VERSION_TAG_REGEX)?.[1];
      if (declaredVersion && metadata.hasCompressedSegmentUris) {
        return `${VERSION_TAG}:${Math.max(
          DEFINE_MINIMUM_VERSION,
          Number(declaredVersion)
        )}`;
      }

      // Any URI attribute left as-is loses its base once this master becomes a
      // data URL.
      return absolutizeUriAttributes(line, manifestUrl);
    })
  );

  return rewrittenLines.filter(line => null !== line).join('\n');
};

// `#EXT-X-STREAM-INF` is a master-playlist-only tag; presence means it is a master, not a media playlist.
const MASTER_PLAYLIST_REGEX = /^#EXT-X-STREAM-INF/m;

/**
 * Fetch an HLS manifest and embed the AES-128 key. A master playlist keeps its
 * full variant ladder; each referenced media playlist is inlined as a data URL.
 *
 * @param {object} parameters
 * @param {string} parameters.manifestUrl - Original HLS manifest URL.
 * @param {string} parameters.clientId
 * @param {string} parameters.sessionId
 * @param {string} parameters.token
 * @param {string} parameters.messageId
 * @returns {Promise<{ dataUrl: string, codecs: string | null }>} Rewritten manifest data URL and fallback codecs.
 * @example
 * createAesEmbeddedManifestSource({ manifestUrl, clientId, sessionId, token, messageId });
 * // => { dataUrl: 'data:application/vnd.apple.mpegurl;base64,...', codecs: 'avc1.64001f' }
 */
const createAesEmbeddedManifestSource = async ({
  manifestUrl,
  clientId,
  sessionId,
  token,
  messageId,
}) => {
  const startedAt = Date.now();
  const metadata = {};
  const prefix = getPlaybackLogPrefix({ messageId });
  const authHeaders = {
    ...getHeaders(),
    ...(clientId && { 'X-Client-ID': clientId }),
    ...(sessionId && { 'X-Session-ID': sessionId }),
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  try {
    const manifestResponse = await fetchWithDeadline(manifestUrl, {
      method: 'GET',
    });
    if (!manifestResponse.ok) {
      throw new Error(
        `aes_embedded_manifest_fetch_error [${manifestResponse.status}] ${manifestUrl}`
      );
    }
    const manifestText = manifestResponse.text;
    // One cache per source build: a key the whole ladder shares is unwrapped
    // once, a ladder with one key per quality unwraps each of them, and no key
    // lives longer than the manifest it belongs to.
    const keyCache = new Map();
    const rewriteParameters = {
      manifestText,
      manifestUrl,
      authHeaders,
      clientId,
      sessionId,
      messageId,
      metadata,
      keyCache,
    };

    // Keep the whole ABR ladder: every media playlist the master references is
    // inlined in place, so the player can still switch tiers.
    const rewritten = MASTER_PLAYLIST_REGEX.test(manifestText)
      ? await rewriteMasterPlaylist(rewriteParameters)
      : await rewriteMediaPlaylist(rewriteParameters);
    const dataUrl = toManifestDataUrl(rewritten);

    playbackBaseLog(`${prefix} source.ready`, {
      messageId,
      sourceType: 'aes-hls',
      sourcePreparationTimeMs: Date.now() - startedAt,
      variantNames: metadata.variantNames || null,
      variantCount: metadata.variantCount || null,
      droppedVariants: metadata.droppedVariants?.length
        ? metadata.droppedVariants
        : null,
      // Set when a playlist declared a target duration its own segments break,
      // so how often the packager ships that defect is answerable from logs.
      repairedTargetDuration: metadata.repairedTargetDuration || null,
      // Whether factoring the segment URIs into an `#EXT-X-DEFINE` paid off,
      // and what the source came to either way: together they say which assets
      // the rewrite reaches and how much it bought them.
      hasCompressedSegmentUris: !!metadata.hasCompressedSegmentUris,
      dataUrlLength: dataUrl.length,
      hlsCodecs: metadata.hlsCodecs || null,
      // How many distinct key URIs this build unwrapped, counting every level
      // that carries one: the video tiers, the audio rendition's own key, and a
      // master `#EXT-X-SESSION-KEY`. Not a quality count — an asset whose ladder
      // shares one key reads 1, and per-quality keys read one per distinct URI
      // plus whatever the audio rendition and the master add.
      keyCount: metadata.keyCount || null,
      keyByteLength: metadata.keyByteLength || null,
    });

    return { dataUrl, codecs: metadata.hlsCodecs || null };
  } catch (error) {
    const stage =
      error.stage ||
      (() => {
        const message = error?.message || '';
        if (message.includes('fetch_error')) return 'manifest.fetch';
        if (message.includes('without_variant')) return 'variant.resolve';
        return 'manifest.rewrite';
      })();

    playbackBaseError(`${prefix} source.failed`, {
      messageId,
      sourceType: 'aes-hls',
      stage,
      errorMessage: error.message,
      sourcePreparationTimeMs: Date.now() - startedAt,
    });

    captureException(error, {
      tags: { scope: 'createAesEmbeddedManifestDataUrl' },
      extra: { manifestUrl },
    });
    throw new Error(`aes_embedded_manifest_failed: ${error.message}`, {
      cause: error,
    });
  }
};

/**
 * Fetch an HLS media playlist and embed the AES-128 key as a data: URI.
 *
 * @param {object} parameters
 * @param {string} parameters.manifestUrl - Original HLS manifest URL.
 * @param {string} parameters.clientId
 * @param {string} parameters.sessionId
 * @param {string} parameters.token
 * @param {string} parameters.messageId
 * @returns {Promise<string>} Data URL of the rewritten manifest.
 * @example
 * createAesEmbeddedManifestDataUrl({ manifestUrl, clientId, sessionId, token, messageId });
 * // => 'data:application/vnd.apple.mpegurl;base64,...'
 */
const createAesEmbeddedManifestDataUrl = async parameters => {
  const { dataUrl } = await createAesEmbeddedManifestSource(parameters);
  return dataUrl;
};

export { createAesEmbeddedManifestSource };
export default createAesEmbeddedManifestDataUrl;
